import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { FolderOpen, Download, CheckCircle2, FileText, FileSignature, Briefcase } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  myDocuments, myDocumentOpen, acknowledgeDocument,
} from '../../../services/api';

const TYPE_META = {
  payslip: { label: 'Payslip', icon: FileText, accent: 'bg-emerald-50 text-emerald-700' },
  contract: { label: 'Contract', icon: FileSignature, accent: 'bg-blue-50 text-blue-700' },
  hr: { label: 'HR', icon: Briefcase, accent: 'bg-amber-50 text-amber-700' },
  other: { label: 'Other', icon: FolderOpen, accent: 'bg-slate-100 text-slate-700' },
};
const TABS = [
  { key: 'all', label: 'All' },
  { key: 'payslip', label: 'Payslips' },
  { key: 'contract', label: 'Contracts' },
  { key: 'hr', label: 'HR' },
];

export default function MyDocumentsHub() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setRows(await myDocuments()); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not load documents'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => tab === 'all' ? rows : rows.filter(r => r.type === tab), [rows, tab]);
  const pendingAck = useMemo(() => rows.filter(r => r.requiresAcknowledgement && !r.acknowledgedAt).length, [rows]);

  const open = async (d) => {
    try { await myDocumentOpen(d.id, d.fileName); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not open document'); }
  };
  const ack = async (d) => {
    try { await acknowledgeDocument(d.id); toast.success('Receipt acknowledged'); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not acknowledge'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="my-documents-hub">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-slate-500 uppercase text-xs tracking-widest">
            <FolderOpen className="h-3.5 w-3.5" />Lutrom Engineering
          </div>
          <h1 className="text-2xl font-display font-bold">My Documents</h1>
          <p className="text-sm text-slate-500 mt-1">Private payslips, contracts and HR documents from Lutrom.</p>
        </div>
        <Link to="/employee/hub" className="text-xs text-slate-500 hover:text-slate-700 mt-2">← Hub</Link>
      </div>

      {pendingAck > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 flex items-center justify-between" data-testid="my-documents-ack-banner">
          <span>You have <b>{pendingAck}</b> document{pendingAck > 1 ? 's' : ''} awaiting acknowledgement.</span>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-slate-200">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-2 text-sm border-b-2 transition-colors ${tab === t.key ? 'border-slate-900 text-slate-900 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`my-documents-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading && <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="p-10 text-center text-slate-500 text-sm">
            <FolderOpen className="h-8 w-8 mx-auto text-slate-300 mb-2" />
            Nothing here yet.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {filtered.map((d, i) => {
            const meta = TYPE_META[d.type] || TYPE_META.other;
            const Icon = meta.icon;
            return (
              <div key={d.id} className="p-4 flex items-center gap-3" data-testid={`my-documents-row-${i}`}>
                <div className={`h-10 w-10 rounded-md flex items-center justify-center shrink-0 ${meta.accent}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{d.title}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {meta.label}{d.period ? ' · ' + d.period : ''} · {(d.createdAt || '').slice(0, 10)}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {d.requiresAcknowledgement && !d.acknowledgedAt && (
                    <Button size="sm" variant="outline" onClick={() => ack(d)}
                      data-testid={`my-documents-ack-${i}`}>
                      Acknowledge
                    </Button>
                  )}
                  {d.acknowledgedAt && (
                    <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" />Acknowledged
                    </span>
                  )}
                  <button onClick={() => open(d)} className="p-2 rounded hover:bg-slate-100 text-slate-700"
                    title="Download" data-testid={`my-documents-open-${i}`}>
                    <Download className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
